/** Minimal design-system primitives, styled by CSS variables (theme.css). */

import { useState } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary";
}

/** A themed button. */
export function Button({
  variant = "primary",
  className,
  type = "button",
  ...rest
}: ButtonProps): ReactNode {
  const cls = ["ui-button", variant === "secondary" ? "ui-button--secondary" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  // A <button> inside a <form> submits by default. Every "Remove", "Add
  // row" and "Cancel" in the editors sat inside a form, so a click saved
  // the old rows and discarded the edit instead (found on the device
  // button editor after the first Vodia import, 2026-10-17). Submitting is
  // opt-in: the Save buttons pass type="submit".
  return <button className={cls} type={type} {...rest} />;
}

/**
 * A destructive action that takes two clicks.
 *
 * The first click arms it, replacing the button with the consequence in
 * words plus an explicit Confirm/Cancel pair. Deliberately inline rather
 * than a modal: nothing steals focus and the row being acted on stays
 * visible, so you can see what you are about to destroy. Nothing is
 * auto-focused — a stray Enter must not confirm a delete.
 */
export function ConfirmButton({
  label = "Delete",
  question,
  confirmLabel = "Yes, delete",
  disabled,
  onConfirm,
}: {
  /** Resting-state button text. */
  label?: string;
  /** What is about to happen, e.g. `Delete jim@example.com?` */
  question: string;
  /** Armed-state confirm text; keep the verb in it. */
  confirmLabel?: string;
  disabled?: boolean;
  onConfirm: () => void;
}): ReactNode {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <Button type="button" variant="secondary" disabled={disabled} onClick={() => setArmed(true)}>
        {label}
      </Button>
    );
  }
  return (
    <span className="ui-confirm" role="group" aria-label={question}>
      <span className="ui-confirm__question">{question}</span>
      <Button
        type="button"
        variant="secondary"
        className="ui-confirm__yes"
        disabled={disabled}
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {confirmLabel}
      </Button>
      <Button type="button" variant="secondary" onClick={() => setArmed(false)}>
        Cancel
      </Button>
    </span>
  );
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
}

/** A labelled text input. */
export function TextField({ label, id, ...rest }: TextFieldProps): ReactNode {
  return (
    <div className="ui-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} {...rest} />
    </div>
  );
}

/** A surface container. */
export function Card({ children }: { children: ReactNode }): ReactNode {
  return <div className="ui-card">{children}</div>;
}

/** An inline message; `error` variant for failures. */
export function Alert({ children, variant = "error" }: { children: ReactNode; variant?: "error" }): ReactNode {
  return (
    <div className={`ui-alert ui-alert--${variant}`} role="alert">
      {children}
    </div>
  );
}

/** An indeterminate loading indicator. */
export function Spinner(): ReactNode {
  return <div className="ui-spinner" role="status" aria-label="Loading" />;
}

export type BadgeTone = "online" | "offline" | "degraded" | "unknown" | "neutral";

/** A small status pill (health, counts). */
export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: BadgeTone }): ReactNode {
  return <span className={`ui-badge ui-badge--${tone}`}>{children}</span>;
}
